import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@progress/kendo-react-buttons';
import MultiSelect from "./MultiSelect";
import Dialog from "../dialogs/Dialog";
import Grid from "../grid/Grid";
import MetaGrid from '../meta/Grid';
import flags from "../utils/flags";
import withMeta from '../utils/withMeta';
import { processAfter, isDateType } from '../utils/helper';
import "./MultiPicker.scss";

const SORT = [];
const PAGE = {
  skip: 0,
  take: 10,
};
const FILTER = { filters: [] };

/**
 * Multi picker component
 * @module
 * @param {array} data - Data
 * @param {string} textField - Text field
 * @param {array} [selected] - Current selected value
 * @param {func} onChange - Function invoked on filter change
 * @param {func} onSelect - Function invoked on value change
 * @param {bool} [required] - Is field required?
 * @param {string} name - Name
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {bool} [disabled] - Is multiselect readonly?
 * @param {number} [tabIndex] - Multiselect tabindex
 * @param {string} [placeholder] - Placeholder
 * @param {string} title - Picker title
 * @param {bool} isLoading - Loading
 * @param {number} total - Total number of items
 * @param {array} schema - Schema for picker
 */
const MultiPickerComponent = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState(props.initFilter || FILTER)
  const [fixedFilter, setFixedFilter] = useState(props.fixedFilter)
  const [selectedItems, setSelectedItems] = useState(props.selected);

  const init = useCallback(async () => {
    if (props.withMeta) {
      const f = await props.meta.getFilters(props.primaryField, props.schema, null, props.db);
      setFixedFilter({ filters: [...(props.fixedFilter ? props.fixedFilter.filters : []), ...f.filter(x => !x.disabled && !x.CanDisabled).map(x => x.filters).flat()] });
      setFilter(prev => props.initFilter || { filters: [...(prev?.filters ?? []), ...f.filter(x => !x.disabled && x.CanDisabled).map(x => x.filters).flat()] })
    }
    // eslint-disable-next-line
  }, [props.primaryField, JSON.stringify(props.initFilter), JSON.stringify(props.fixedFilter), props.db, props.meta, JSON.stringify(props.schema), JSON.stringify(props.selected), props.textField, props.withMeta])

  useEffect(() => {
    init();
  }, [init]);

  const handleOpen = (event) => {
    event.preventDefault();
    setIsOpen(true);
    setSelectedItems(props.selected);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleFilter = (filter) => {
    setFilter(filter);
  };

  const handleSelect = (selected, isCopy, selectedAll) => {
    setSelectedItems([...selected, ...selectedAll]);
  };

  const handleConfirm = () => {
    setIsOpen(false);
    setFilter(FILTER);
    props.onSelect(props.name, selectedItems);
  };

  const handleChange = (filter, sort, page) => {
    setFilter(filter)
    props.onChange(filter, sort || SORT, page || PAGE, props.primaryField);
  };

  const getFilter = () => {
    const filterObj = filter.filters.find((el) => el.field === props.textField);
    return filterObj ? filterObj.value : null;
  };

  const handleReducer = async (action, data, state, frmState) => {
    switch (action) {
      case "SELECT":
        handleSelect(state[state.primaryField]?.selected);
        break;
      case "CREATE":
        setSelectedItems({ ...data.data });
        await handleConfirm();
        break;
      default:
        break;
    }
    if (props.reducer) {
      return await props.reducer(action, data, state, frmState);
    } else {
      return state;
    }
  };

  const gridDateColumns = props.schema.filter(x => isDateType(x.filter));

  return (
    <div className={`form-input multipicker ${props.disabled ? "disabled-field" : props.required ? "required-field" : "default-field"}`} >
      <MultiSelect
        name={props.name}
        data={props.data}
        onChange={props.onSelect}
        value={props.selected}
        textField={props.textField}
        dataItemKey={props.textField}
        disabled={props.disabled}
        required={props.required}
        validationMessage={props.validationMessage}
        tabIndex={props.tabIndex}
        onBlur={props.onBlur}
        onFilterChange={handleChange}
        placeholder={props.placeholder}
        filter={getFilter()}
      />
      <Button
        type="button"
        onClick={handleOpen}
        icon="more-vertical"
        disabled={props.disabled}
      />
      <Dialog
        isOpen={props.externalIsOpen || isOpen}
        title={props.title}
        onClose={handleClose}
        onConfirm={handleConfirm}
        confirmDisabled={!selectedItems}
      >
        {props.detail ?
          <MetaGrid
            primaryField={props.primaryField}
            componentFactory={() => { return null; }}
            akcFactory={props.akcFactory}
            reducer={handleReducer}
            selected={props.selected}
            initFilter={filter}
            fixedFilter={fixedFilter}
            db={props.db}
          />
          :
          <Grid
            total={props.total}
            data={gridDateColumns.length > 0 ? props.data.map(x => { return processAfter(gridDateColumns, x) }) : props.data}
            isLoading={props.isLoading}
            onChange={handleChange}
            schema={props.schema}
            multiple={true}
            toolbarHidden={true}
            filter={filter}
            onFilter={handleFilter}
            onSelect={handleSelect}
            primaryField={props.primaryField}
            onConfirm={handleConfirm}
            identif={props.identif}
            isPicker
            parentProps={props.parentProps}
            selected={props.selected}
            currentDataItem={props.currentDataItem}
            hidden={flags.PRINT | flags.RESET | flags.LEFT_TOOLBAR | flags.EDITCELL}
            withMeta={props.withMeta}
            fixedFilter={fixedFilter}
            db={props.db}
            rememberSelection={true}
          />
        }
      </Dialog>
    </div>
  );
};

MultiPickerComponent.propTypes = {
  data: PropTypes.array.isRequired,
  textField: PropTypes.string.isRequired,
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  required: PropTypes.bool,
  name: PropTypes.string.isRequired,
  validationMessage: PropTypes.string,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  title: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  total: PropTypes.number.isRequired,
  schema: PropTypes.array.isRequired,
};

export default withMeta(MultiPickerComponent);
